// src/components/label/index.tsx
import React from "react";
import { label } from "@pinatagrams/design-system/recipes";
import { jsx, jsxs } from "react/jsx-runtime";
var LabelRoot = React.forwardRef(
  ({ width = 8, height = 4, unit = "in", className, style, children, ...props }, ref) => {
    const customStyle = {
      "--label-width": String(width),
      "--label-height": String(height),
      "--label-unit": `1${unit}`,
      ...style
    };
    return /* @__PURE__ */ jsx(
      "div",
      {
        ref,
        className: `${label({}).root}${className ? ` ${className}` : ""}`,
        style: customStyle,
        ...props,
        children
      }
    );
  }
);
LabelRoot.displayName = "Label";
var LabelGroup = React.forwardRef(
  ({ className, children, ...props }, ref) => {
    return /* @__PURE__ */ jsx("div", { ref, className: `${label({}).group}${className ? ` ${className}` : ""}`, ...props, children });
  }
);
LabelGroup.displayName = "Label.Group";
var LabelDetails = React.forwardRef(
  ({ className, children, ...props }, ref) => {
    return /* @__PURE__ */ jsx(
      "div",
      {
        ref,
        className: `${label({}).details}${className ? ` ${className}` : ""}`,
        ...props,
        children
      }
    );
  }
);
LabelDetails.displayName = "Label.Details";
var LabelSection = React.forwardRef(
  ({ variant = "default", className, children, ...props }, ref) => {
    return /* @__PURE__ */ jsx(
      "section",
      {
        ref,
        className: `${label({ sectionVariant: variant }).section}${className ? ` ${className}` : ""}`,
        ...props,
        children
      }
    );
  }
);
LabelSection.displayName = "Label.Section";
var LabelSku = React.forwardRef(
  ({ emoji, className, children, ...props }, ref) => {
    return /* @__PURE__ */ jsxs("h2", { ref, className: `${label({}).sku}${className ? ` ${className}` : ""}`, ...props, children: [
      emoji && /* @__PURE__ */ jsx("span", { className: label({}).emoji, children: emoji }),
      /* @__PURE__ */ jsx("span", { children }),
      emoji && /* @__PURE__ */ jsx("span", { className: label({}).emoji, children: emoji })
    ] });
  }
);
LabelSku.displayName = "Label.Sku";
var LabelPill = React.forwardRef(
  ({ className, children, ...props }, ref) => {
    return /* @__PURE__ */ jsx("div", { ref, className: `${label({}).pill}${className ? ` ${className}` : ""}`, ...props, children });
  }
);
LabelPill.displayName = "Label.Pill";
var LabelBulk = React.forwardRef(
  ({ className, children, ...props }, ref) => {
    return /* @__PURE__ */ jsx("div", { ref, className: `${label({}).bulk}${className ? ` ${className}` : ""}`, ...props, children });
  }
);
LabelBulk.displayName = "Label.Bulk";
var LabelBulkBox = React.forwardRef(({ className, children, ...props }, ref) => {
  return /* @__PURE__ */ jsx(
    "span",
    {
      ref,
      className: `${label({}).bulkBox}${className ? ` ${className}` : ""}`,
      ...props,
      children
    }
  );
});
LabelBulkBox.displayName = "Label.BulkBox";
var LabelDivider = React.forwardRef(
  ({ highlight = false, className, ...props }, ref) => {
    return /* @__PURE__ */ jsx(
      "div",
      {
        ref,
        className: `${label({ dividerHighlight: highlight }).divider}${className ? ` ${className}` : ""}`,
        "data-highlight": highlight,
        ...props
      }
    );
  }
);
LabelDivider.displayName = "Label.Divider";
var LabelName = React.forwardRef(({ className, children, ...props }, ref) => {
  return /* @__PURE__ */ jsx("h5", { ref, className: `${label({}).name}${className ? ` ${className}` : ""}`, ...props, children });
});
LabelName.displayName = "Label.Name";
var LabelAddress = React.forwardRef(({ className, children, ...props }, ref) => {
  return /* @__PURE__ */ jsx("p", { ref, className: `${label({}).address}${className ? ` ${className}` : ""}`, ...props, children });
});
LabelAddress.displayName = "Label.Address";
var LabelMessage = React.forwardRef(
  ({ className, children, ...props }, ref) => {
    return /* @__PURE__ */ jsx(
      "pre",
      {
        ref,
        className: `${label({}).message}${className ? ` ${className}` : ""}`,
        ...props,
        children
      }
    );
  }
);
LabelMessage.displayName = "Label.Message";
var LabelId = React.forwardRef(
  ({ className, children, ...props }, ref) => {
    const dataExternal = props["data-external"] === "true";
    const dataOverdue = props["data-overdue"] === "true";
    return /* @__PURE__ */ jsx(
      "pre",
      {
        ref,
        className: `${label({ idExternal: dataExternal, idOverdue: dataOverdue }).id}${className ? ` ${className}` : ""}`,
        ...props,
        children
      }
    );
  }
);
LabelId.displayName = "Label.Id";
var LabelGraphic = React.forwardRef(
  ({ className, alt = "Label graphic", ...props }, ref) => {
    return /* @__PURE__ */ jsx(
      "img",
      {
        ref,
        className: `${label({}).graphic}${className ? ` ${className}` : ""}`,
        alt,
        loading: "eager",
        ...props
      }
    );
  }
);
LabelGraphic.displayName = "Label.Graphic";
var LabelMessageOverlay = React.forwardRef(({ className, children, ...props }, ref) => {
  return /* @__PURE__ */ jsx(
    "div",
    {
      ref,
      className: `${label({}).messageOverlay}${className ? ` ${className}` : ""}`,
      ...props,
      children
    }
  );
});
LabelMessageOverlay.displayName = "Label.MessageOverlay";
var LabelMessageText = React.forwardRef(({ className, children, style, onChange, onBlur, onFocus, onInput, ...props }, ref) => {
  const messageTextClass = `${label({}).messageText}${className ? ` ${className}` : ""}`;
  const hasCallbacks = Boolean(onChange || onBlur || onFocus || onInput);
  const textareaProps = props;
  if (hasCallbacks) {
    return /* @__PURE__ */ jsx(
      "textarea",
      {
        ref,
        className: messageTextClass,
        style: {
          ...style
        },
        onChange,
        onBlur,
        onFocus,
        onInput,
        ...textareaProps
      }
    );
  }
  return /* @__PURE__ */ jsx(
    "div",
    {
      ref,
      className: messageTextClass,
      style,
      ...props,
      children
    }
  );
});
LabelMessageText.displayName = "Label.MessageText";
var Label = Object.assign(LabelRoot, {
  Group: LabelGroup,
  Details: LabelDetails,
  Section: LabelSection,
  Sku: LabelSku,
  Pill: LabelPill,
  Bulk: LabelBulk,
  BulkBox: LabelBulkBox,
  Divider: LabelDivider,
  Name: LabelName,
  Address: LabelAddress,
  Message: LabelMessage,
  Id: LabelId,
  Graphic: LabelGraphic,
  MessageOverlay: LabelMessageOverlay,
  MessageText: LabelMessageText
});
var label_default = Label;
export {
  Label,
  LabelAddress,
  LabelBulk,
  LabelBulkBox,
  LabelDetails,
  LabelDivider,
  LabelGraphic,
  LabelGroup,
  LabelId,
  LabelMessage,
  LabelMessageOverlay,
  LabelMessageText,
  LabelName,
  LabelPill,
  LabelRoot,
  LabelSection,
  LabelSku,
  label_default as default
};
//# sourceMappingURL=label.js.map